import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useSession } from '@/context/AuthContext'
import { LayoutDashboard, FolderKanban, Lightbulb, LogOut, Boxes, ChevronDown, ShieldCheck, UserCog, Building2, ClipboardCheck, Sun, Moon, History, UserRound, LayoutGrid, GanttChartSquare, Rocket, Bell, Ticket as TicketIcon } from 'lucide-react'
import { useNotifCount } from '@/lib/useNotifCount'
import { roleLevel } from '@/lib/roles'
import { LEAD_EMAIL } from '@/lib/dev-team'
import { deptColor, INTERNAL_DIVISIONS } from '@/lib/departments'
import { CONTENT_PLANS_CACHE_PREFIX } from '@/lib/useContentPlans'

// Menu dikelompokkan jadi accordion per "system" (klik → dropdown sub-menu).
// Untuk sekarang baru satu grup (Project); nanti tinggal tambah grup baru di
// array ini untuk modul internal lainnya.
const NAV_GROUPS = [
  {
    label: 'Project',
    icon: Boxes,
    items: [
      { href: '/',           label: 'Dashboard',          icon: LayoutDashboard, match: (p: string) => p === '/' },
      { href: '/projects',   label: 'Project Management',  icon: FolderKanban,    match: (p: string) => p.startsWith('/projects') },
      { href: '/timeline',   label: 'Timeline',            icon: GanttChartSquare, match: (p: string) => p.startsWith('/timeline') },
      { href: '/content-plan',  label: 'Content & Calendar', icon: LayoutGrid,    match: (p: string) => p.startsWith('/content-plan') || p.startsWith('/calendar-plan') },
      { href: '/history-log', label: 'History Log',         icon: History,         match: (p: string) => p.startsWith('/history-log') },
      { href: '/approvals',  label: 'Persetujuan',         icon: ClipboardCheck,  match: (p: string) => p.startsWith('/approvals') },
    ],
  },
]

// Grup khusus admin (flag is_admin) — hanya ditambahkan kalau user admin.
const ADMIN_GROUP = {
  label: 'Admin',
  icon: ShieldCheck,
  items: [
    { href: '/users', label: 'User Management', icon: UserCog, match: (p: string) => p.startsWith('/users') },
  ],
}

export function AppSidebar() {
  const pathname = useLocation().pathname
  const [expanded, setExpanded] = useState(false)
  const { data: session } = useSession()

  // Dark mode — class 'dark' di <html>, preferensi disimpan di localStorage.
  const [dark, setDark] = useState(false)
  useEffect(() => { setDark(document.documentElement.classList.contains('dark')) }, [])
  const toggleTheme = () => {
    const next = !document.documentElement.classList.contains('dark')
    document.documentElement.classList.toggle('dark', next)
    try { localStorage.setItem('jal-theme', next ? 'dark' : 'light') } catch { /* abaikan */ }
    setDark(next)
  }

  // Pemegang flag admin dapat grup menu tambahan (RBAC v2: flag, bukan role).
  const isAdmin = session?.user?.isAdmin === true
  const department = session?.user?.department ?? null
  const dc = deptColor(department)
  // Badge role di sebelah badge BU. Ikon mengikuti jenjang: admin > pimpinan > staff.
  const role = session?.user?.role ?? null
  const RoleIcon = isAdmin ? ShieldCheck : roleLevel(role) >= 1 ? UserCog : UserRound

  // Entri Dev Sprint (/dev) — HANYA muncul untuk lead (Brian). Lainnya via direct link.
  const isLeadUser = (session?.user?.email ?? '').toLowerCase() === LEAD_EMAIL
  const projectGroup = isLeadUser
    ? { ...NAV_GROUPS[0], items: [...NAV_GROUPS[0].items, { href: '/dev', label: 'Dev Sprint', icon: Rocket, match: (p: string) => p.startsWith('/dev') }] }
    : NAV_GROUPS[0]
  const baseGroups = [projectGroup, ...NAV_GROUPS.slice(1)]
  const groups = isAdmin ? [...baseGroups, ADMIN_GROUP] : baseGroups

  // Badge notifikasi penyelesaian (realtime). Hook ini juga jadi sumber SSE.
  const notifCount = useNotifCount()

  // Badge jumlah pending: admin = antrian ACC; member = pengajuan sendiri yg masih nunggu.
  const [approvalCount, setApprovalCount] = useState(0)
  useEffect(() => {
    let active = true
    const load = async () => {
      try {
        const res = await fetch('/api/approvals')
        if (!res.ok) return
        const d = await res.json()
        const cnt = d.isAdmin
          ? (d.review?.projects?.length ?? 0) + (d.review?.ideas?.length ?? 0)
          : [...(d.mine?.projects ?? []), ...(d.mine?.ideas ?? [])]
              .filter((x: { status?: string }) => ['pending', 'new', 'reviewing'].includes(x.status ?? '')).length
        if (active) setApprovalCount(cnt)
      } catch { /* abaikan */ }
    }
    load()
    const t = setInterval(load, 45000)
    return () => { active = false; clearInterval(t) }
  }, [])

  // Badge Ticketing = jumlah request menunggu triase DALAM CAKUPAN viewer.
  // Dulu dikunci ke departemen HOLDING; meja tiket kini dijalankan tiap divisi & unit
  // bisnis (gerbangnya level, bukan keanggotaan holding), jadi badge ini berlaku umum.
  // `summary` hanya dikirim ke viewer lintas unit — untuk sisanya hitung dari daftarnya.
  const [ticketPending, setTicketPending] = useState(0)
  // Jumlah notifikasi belum dibaca — badge di menu Notifikasi.
  const [unreadNotif, setUnreadNotif] = useState(0)
  useEffect(() => {
    let active = true
    const load = async () => {
      try {
        const res = await fetch('/api/notifications')
        if (!res.ok) return
        const d = await res.json()
        if (active) setUnreadNotif(Number(d?.unread ?? 0))
      } catch { /* abaikan */ }
    }
    load()
    const t = setInterval(load, 45000)
    return () => { active = false; clearInterval(t) }
  }, [pathname])
  useEffect(() => {
    let active = true
    const load = async () => {
      try {
        const res = await fetch('/api/ticketing')
        if (!res.ok) return
        const d = await res.json()
        const fallback = Array.isArray(d?.tickets)
          ? d.tickets.filter((t: { status?: string }) => t.status === 'pending').length
          : 0
        if (active) setTicketPending(d?.summary?.pendingTotal ?? fallback)
      } catch { /* abaikan */ }
    }
    load()
    const t = setInterval(load, 45000)
    return () => { active = false; clearInterval(t) }
  }, [department])

  // Accordion: grup terbuka otomatis kalau salah satu sub-menunya sedang aktif
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {}
    for (const g of [...NAV_GROUPS, ADMIN_GROUP]) init[g.label] = g.items.some((it) => it.match(pathname))
    return init
  })
  const toggleGroup = (label: string) =>
    setOpenGroups((s) => ({ ...s, [label]: !s[label] }))

  // Jangan tampilkan sidebar di halaman login
  if (pathname === '/login') return null

  const user = session?.user
  const initial = (user?.name ?? user?.email ?? '?').charAt(0).toUpperCase()

  return (
    <aside
      onMouseEnter={() => setExpanded(true)}
      onMouseLeave={() => setExpanded(false)}
      style={{ width: expanded ? '212px' : '56px' }}
      className="fixed left-0 top-0 h-full z-40 bg-white border-r border-[#E8E7E3] flex flex-col overflow-hidden transition-[width] duration-200 ease-in-out"
    >
      {/* Brand */}
      <div
        className="flex items-center gap-3 px-3.5 border-b border-[#E8E7E3] flex-shrink-0"
        style={{ height: '52px', minWidth: '212px' }}
      >
        <div className="w-7 h-7 rounded-full overflow-hidden ring-1 ring-[#E0DFD8] flex-shrink-0">
          <img src="/logo.jpg" alt="Logo" width={28} height={28}
            className="w-full h-full object-cover" style={{ filter: 'invert(1)' }} />
        </div>
        <div className="overflow-hidden min-w-0">
          <p className="font-display text-[11px] font-bold text-[#1A1916] leading-tight whitespace-nowrap transition-opacity duration-150"
            style={{ opacity: expanded ? 1 : 0 }}>
            Sistem Internal
          </p>
          <p className="font-display text-[11px] font-bold text-[#6366F1] leading-tight whitespace-nowrap transition-opacity duration-150"
            style={{ opacity: expanded ? 1 : 0 }}>
            JAL
          </p>
        </div>
      </div>

      {/* Badge departemen + role — tepat di bawah brand */}
      {expanded && (department || role) && (
        <div className="px-3.5 pt-3 pb-1 flex flex-wrap items-center gap-1.5" style={{ minWidth: '212px' }}>
          {department && (
            <span
              title={`${(INTERNAL_DIVISIONS as readonly string[]).includes(department) ? 'Divisi' : department === 'HOLDING' ? 'Induk' : 'Unit bisnis'}: ${department}`}
              className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-body font-bold uppercase tracking-wider border whitespace-nowrap"
              style={{ backgroundColor: dc.bg, color: dc.text, borderColor: dc.border }}
            >
              <Building2 className="w-3 h-3" />
              {department}
            </span>
          )}
          {role && (
            <span
              title={`Role: ${role}`}
              className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-body font-bold uppercase tracking-wider border whitespace-nowrap ${
                isAdmin
                  ? 'bg-[#1A1916] text-white border-[#1A1916]'
                  : 'bg-[#F4F3F0] text-[#3A3A36] border-[#E0DFD8]'
              }`}
            >
              <RoleIcon className="w-3 h-3" />
              {role}
            </span>
          )}
        </div>
      )}

      {/* Nav — scroll saat menu/accordion lebih tinggi dari viewport (min-h-0 wajib
          agar flex child bisa mengecil & scroll, bukan mendorong footer kepotong). */}
      <nav className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden py-3 space-y-0.5" style={{ minWidth: '212px' }}>
        <div>
          <Link
            to="/ticketing"
            title={expanded ? undefined : 'Ticketing'}
            className={`flex items-center gap-3 mx-2 px-2.5 py-2.5 rounded-xl text-[13px] font-body font-medium transition-all duration-150 ${
              pathname.startsWith('/ticketing') ? 'bg-[#1A1916] text-white' : 'text-[#3A3A36] hover:bg-[#F4F3F0]'
            }`}
            style={{ width: 'calc(100% - 16px)' }}
          >
            <span className="relative flex-shrink-0">
              <TicketIcon className="w-4 h-4" />
              {ticketPending > 0 && (
                <span className="absolute -top-1.5 -right-1.5 min-w-[15px] h-[15px] px-1 rounded-full bg-[#DC2626] text-white text-[9px] font-bold flex items-center justify-center ring-2 ring-white">
                  {ticketPending > 9 ? '9+' : ticketPending}
                </span>
              )}
            </span>
            <span className="flex-1 text-left whitespace-nowrap transition-opacity duration-150" style={{ opacity: expanded ? 1 : 0 }}>
              Ticketing
            </span>
          </Link>
        </div>

        {/* Notifikasi — apa pun yang ditujukan langsung ke user ini */}
        <div>
          <Link
            to="/notifications"
            title={expanded ? undefined : 'Notifikasi'}
            className={`flex items-center gap-3 mx-2 px-2.5 py-2.5 rounded-xl text-[13px] font-body font-medium transition-all duration-150 ${
              pathname.startsWith('/notifications') ? 'bg-[#1A1916] text-white' : 'text-[#3A3A36] hover:bg-[#F4F3F0]'
            }`}
            style={{ width: 'calc(100% - 16px)' }}
          >
            <span className="relative flex-shrink-0">
              <Bell className="w-4 h-4" />
              {unreadNotif > 0 && (
                <span className="absolute -top-1.5 -right-1.5 min-w-[15px] h-[15px] px-1 rounded-full bg-[#DC2626] text-white text-[9px] font-bold flex items-center justify-center ring-2 ring-white">
                  {unreadNotif > 9 ? '9+' : unreadNotif}
                </span>
              )}
            </span>
            <span className="flex-1 text-left whitespace-nowrap transition-opacity duration-150" style={{ opacity: expanded ? 1 : 0 }}>
              Notifikasi
            </span>
          </Link>
        </div>

        {/* Ide Board — satu blok dengan Ticketing, dipisah dari My Dashboard */}
        <div className="pb-2 mb-2 border-b border-[#E0DFD8]">
          <Link
            to="/ideas"
            title={expanded ? undefined : 'Ide Board'}
            className={`flex items-center gap-3 mx-2 px-2.5 py-2.5 rounded-xl text-[13px] font-body font-medium transition-all duration-150 ${
              pathname.startsWith('/ideas') ? 'bg-[#1A1916] text-white' : 'text-[#3A3A36] hover:bg-[#F4F3F0]'
            }`}
            style={{ width: 'calc(100% - 16px)' }}
          >
            <span className="flex-shrink-0"><Lightbulb className="w-4 h-4" /></span>
            <span
              className="flex-1 text-left whitespace-nowrap transition-opacity duration-150"
              style={{ opacity: expanded ? 1 : 0 }}
            >
              Ide Board
            </span>
          </Link>
        </div>

        {/* My Dashboard — menu utama personal, di atas grup Project */}
        <div className="pb-2 mb-2 border-b border-[#E0DFD8]">
          <Link
            to="/my-dashboard"
            title={expanded ? undefined : 'My Dashboard'}
            className={`flex items-center gap-3 mx-2 px-2.5 py-2.5 rounded-xl text-[13px] font-body font-medium transition-all duration-150 ${
              pathname.startsWith('/my-dashboard') ? 'bg-[#1A1916] text-white' : 'text-[#3A3A36] hover:bg-[#F4F3F0]'
            }`}
            style={{ width: 'calc(100% - 16px)' }}
          >
            <span className="flex-shrink-0"><UserRound className="w-4 h-4" /></span>
            <span
              className="flex-1 text-left whitespace-nowrap transition-opacity duration-150"
              style={{ opacity: expanded ? 1 : 0 }}
            >
              My Dashboard
            </span>
          </Link>
        </div>
        {groups.map((group) => {
          const open = openGroups[group.label]
          const hasActiveChild = group.items.some((it) => it.match(pathname))
          const groupHasApprovals = group.items.some((it) => it.href === '/approvals')
          const groupHasNotif = group.items.some((it) => it.href === '/history-log')
          const groupDot = (groupHasApprovals && approvalCount > 0) || (groupHasNotif && notifCount > 0)
          return (
            <div key={group.label} className="mb-1">
              {/* Parent — klik untuk buka/tutup dropdown */}
              <button
                onClick={() => toggleGroup(group.label)}
                title={expanded ? undefined : group.label}
                className={`flex items-center gap-3 mx-2 px-2.5 py-2.5 rounded-xl text-[13px] font-body font-medium transition-all duration-150 ${
                  hasActiveChild && (!open || !expanded)
                    ? 'bg-[#1A1916] text-white'
                    : 'text-[#3A3A36] hover:bg-[#F4F3F0]'
                }`}
                style={{ width: 'calc(100% - 16px)' }}
              >
                <span className="relative flex-shrink-0">
                  <group.icon className="w-4 h-4" />
                  {groupDot && (!expanded || !open) && (
                    <span className="absolute -top-1.5 -right-1.5 w-2 h-2 rounded-full bg-[#DC2626] ring-2 ring-white" />
                  )}
                </span>
                <span
                  className="flex-1 text-left whitespace-nowrap transition-opacity duration-150"
                  style={{ opacity: expanded ? 1 : 0 }}
                >
                  {group.label}
                </span>
                <ChevronDown
                  className={`w-3.5 h-3.5 flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
                  style={{ opacity: expanded ? 1 : 0 }}
                />
              </button>

              {/* Sub-menu — hanya tampil saat sidebar expanded (hindari kepotong saat collapsed) */}
              {open && expanded && (
                <div className="mt-0.5 ml-[18px] pl-2 border-l border-[#F0EFE9] space-y-0.5">
                  {group.items.map((item) => {
                    const active = item.match(pathname)
                    return (
                      <Link
                        key={item.href}
                        to={item.href}
                        title={expanded ? undefined : item.label}
                        className={`flex items-center gap-2.5 mx-1 px-2.5 py-2 rounded-lg text-[12.5px] font-body font-medium transition-all duration-150 ${
                          active
                            ? 'bg-[#1A1916] text-white'
                            : 'text-[#6B6860] hover:bg-[#F4F3F0] hover:text-[#1A1916]'
                        }`}
                      >
                        <item.icon className="w-3.5 h-3.5 flex-shrink-0" />
                        <span
                          className="whitespace-nowrap transition-opacity duration-150"
                          style={{ opacity: expanded ? 1 : 0 }}
                        >
                          {item.label}
                        </span>
                        {item.href === '/approvals' && approvalCount > 0 && (
                          <span
                            className="ml-auto min-w-[16px] h-4 px-1 rounded-full bg-[#DC2626] text-white text-[9px] font-bold flex items-center justify-center transition-opacity duration-150"
                            style={{ opacity: expanded ? 1 : 0 }}
                          >
                            {approvalCount > 9 ? '9+' : approvalCount}
                          </span>
                        )}
                        {item.href === '/history-log' && notifCount > 0 && (
                          <span
                            className="ml-auto min-w-[16px] h-4 px-1 rounded-full bg-[#DC2626] text-white text-[9px] font-bold flex items-center justify-center transition-opacity duration-150"
                            style={{ opacity: expanded ? 1 : 0 }}
                          >
                            {notifCount > 9 ? '9+' : notifCount}
                          </span>
                        )}
                      </Link>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </nav>

      {/* Toggle dark mode — tepat di atas profil */}
      <div className="px-3 pt-2 pb-1 border-t border-[#F4F3F0]" style={{ minWidth: '212px' }}>
        <button
          onClick={toggleTheme}
          title={expanded ? undefined : (dark ? 'Mode terang' : 'Mode gelap')}
          aria-label={dark ? 'Aktifkan mode terang' : 'Aktifkan mode gelap'}
          className="flex items-center gap-3 px-2.5 py-2.5 rounded-xl text-[13px] font-body font-medium text-[#3A3A36] hover:bg-[#F4F3F0] transition-all duration-150"
          style={{ width: 'calc(100% - 0px)' }}
        >
          <span className="flex-shrink-0">
            {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </span>
          <span
            className="flex-1 text-left whitespace-nowrap transition-opacity duration-150"
            style={{ opacity: expanded ? 1 : 0 }}
          >
            {dark ? 'Mode Terang' : 'Mode Gelap'}
          </span>
        </button>
      </div>

      {/* Footer — user + logout */}
      <div className="px-3 py-3 border-t border-[#F4F3F0]" style={{ minWidth: '212px' }}>
        <div className="flex items-center gap-2.5 px-1.5">
          <div className="w-7 h-7 rounded-full bg-[#1A1916] text-white flex items-center justify-center text-xs font-bold flex-shrink-0 overflow-hidden">
            {user?.image ? (
              <img src={user.image} alt={user?.name ?? 'User'} width={28} height={28} className="w-full h-full object-cover" />
            ) : (
              initial
            )}
          </div>
          <div
            className="flex-1 min-w-0 transition-opacity duration-150"
            style={{ opacity: expanded ? 1 : 0 }}
          >
            <p className="text-[12px] font-body font-semibold text-[#1A1916] truncate leading-tight">
              {user?.name ?? 'Pengguna'}
            </p>
            <p className="text-[10px] text-[#AEADA8] font-body truncate leading-tight">
              {user?.email ?? ''}
            </p>
          </div>
          <button
            onClick={() => {
              sessionStorage.removeItem('jal_play_intro')
              // Bersihkan cache data per-user agar tidak bocor ke akun lain di browser sama.
              try { Object.keys(localStorage).filter((k) => k.startsWith('jal_cache_') || k.startsWith(CONTENT_PLANS_CACHE_PREFIX)).forEach((k) => localStorage.removeItem(k)) } catch { /* abaikan */ }
              window.location.href = '/auth/logout'
            }}
            title="Keluar"
            className="p-1.5 rounded-lg text-[#C0BDB6] hover:text-[#DC2626] hover:bg-[#FEF2F2] transition-colors flex-shrink-0"
            style={{ opacity: expanded ? 1 : 0 }}
          >
            <LogOut className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </aside>
  )
}
